import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Download, Sparkles, Send, RotateCcw, Archive, Trash2 } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { formatINR, fmtDateTime } from "../format";
import { fmtPct } from "../fincalc";

const AUD_LABEL = { board: "Board", investors: "Investors", ops: "Operations" };
const HEALTH_LABEL = { ok: "On track", watch: "Watch", risk: "At risk" };

function Sheet({ onClose, title, children, testId, wide }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" style={wide ? { width: "min(860px, 94vw)" } : undefined}
        role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function CommentaryEditor({ report, onSaved, onClose }) {
  const c = report.commentary || {};
  const [f, setF] = useState({
    headline: c.headline || "",
    summary: c.summary || "",
    highlights: (c.highlights || []).join("\n"),
    risks: (c.risks || []).join("\n"),
    asks: (c.asks || []).join("\n"),
    outlook: c.outlook || "",
  });
  const [busy, setBusy] = useState(false);
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      await cmdApi.patch(`/reports/${report.id}`, {
        commentary: {
          headline: f.headline, summary: f.summary,
          highlights: lines(f.highlights), risks: lines(f.risks),
          asks: lines(f.asks), outlook: f.outlook,
        },
      });
      toast.success("Commentary saved");
      onSaved();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  const ta = (key, label, rows, testId) => (
    <div className="cmd-field" key={key}>
      <label>{label}</label>
      <textarea className="cmd-input" rows={rows} value={f[key]}
        onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))} data-testid={testId} />
    </div>
  );
  return (
    <div data-testid="commentary-editor">
      {ta("headline", "Headline", 2, "com-headline")}
      {ta("summary", "Summary", 4, "com-summary")}
      {ta("highlights", "Highlights — one bullet per line", 4, "com-highlights")}
      {ta("risks", "Risks — one bullet per line", 3, "com-risks")}
      {ta("asks", "Asks — one bullet per line", 3, "com-asks")}
      {ta("outlook", "Outlook", 2, "com-outlook")}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="cmd-btn cmd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="cmd-btn cmd-btn-primary" onClick={save} disabled={busy} data-testid="com-save">
          {busy ? "Saving…" : "Save commentary"}
        </button>
      </div>
    </div>
  );
}

function ReportPreview({ report }) {
  const s = report.snapshot || {};
  const c = report.commentary || {};
  const fin = s.financials;
  const headline = c.headline || s.auto_headline;
  return (
    <div className="cmd-report-preview" data-testid="report-preview">
      <div className="cmd-report-meta">
        {report.title}
        {report.commentary_ai && (
          <span className="cmd-chip" style={{ marginLeft: 8 }} data-testid="ai-meta">
            Commentary drafted with AI{report.commentary_reviewed_by ? `, reviewed by ${report.commentary_reviewed_by}` : ""}
          </span>
        )}
      </div>
      <h2 className="cmd-report-headline" data-testid="report-headline">{headline}</h2>
      {c.summary && <p className="cmd-report-summary">{c.summary}</p>}

      <h4>Portfolio</h4>
      <p className="cmd-muted" style={{ fontSize: 13 }}>
        {s.portfolio?.by_stage?.running ?? 0} running · {s.portfolio?.by_stage?.building ?? 0} building · {s.portfolio?.by_stage?.pipeline ?? 0} pipeline
        {" — "}
        {s.portfolio?.by_health?.ok ?? 0} on track · {s.portfolio?.by_health?.watch ?? 0} watch · {s.portfolio?.by_health?.risk ?? 0} at risk · {s.portfolio?.by_health?.unrated ?? 0} unrated
      </p>

      {fin ? (
        <>
          <h4>Financials · {s.month_label}</h4>
          <p style={{ fontSize: 14 }}>
            Revenue {formatINR(fin.month?.revenue)} · GM {fmtPct(fin.month?.gm_pct)} · EBITDA {formatINR(fin.month?.ebitda)}
            · Cash {formatINR(fin.cash_balance)} · Receivables {formatINR(fin.receivables)} · Payables {formatINR(fin.payables)}
          </p>
          <p className="cmd-muted" style={{ fontSize: 13 }}>
            {s.fy} to date: revenue {formatINR(fin.fytd?.revenue)}, EBITDA {formatINR(fin.fytd?.ebitda)}
          </p>
        </>
      ) : (
        <p className="cmd-muted" style={{ fontSize: 14 }} data-testid="figures-pending">
          Figures for {s.month_label} are pending.
        </p>
      )}

      {!!c.highlights?.length && (
        <><h4>Highlights</h4><ul className="cmd-report-list">{c.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul></>
      )}
      {!!c.risks?.length && (
        <><h4>Risks</h4><ul className="cmd-report-list">{c.risks.map((r, i) => <li key={i}>{r}</li>)}</ul></>
      )}

      <h4>Decisions</h4>
      <p className="cmd-muted" style={{ fontSize: 13 }}>
        {s.decisions?.closed?.length ?? 0} closed this month · {s.decisions?.open?.length ?? 0} awaiting a call
      </p>

      {s.execution && (
        <>
          <h4>Execution</h4>
          <p className="cmd-muted" style={{ fontSize: 13 }}>
            {s.execution.done_count} completed · {s.execution.late?.length ?? 0} late · {s.execution.next_30_days?.length ?? 0} due in 30 days
          </p>
        </>
      )}

      {!!c.asks?.length && (
        <><h4>Asks</h4><ul className="cmd-report-list">{c.asks.map((a, i) => <li key={i}>{a}</li>)}</ul></>
      )}
      {c.outlook && <><h4>Outlook</h4><p className="cmd-report-summary">{c.outlook}</p></>}

      {s.shareholders && (
        <>
          <h4>Shareholding</h4>
          <p className="cmd-muted" style={{ fontSize: 13 }}>
            {s.shareholders.items.length} shareholders · total invested {formatINR(s.shareholders.total_invested)}
          </p>
        </>
      )}
    </div>
  );
}

export default function CmdReports() {
  const { can, isFounder, role } = usePermissions();
  const canEdit = can("reports", "edit");
  const isInvestor = role === "Investor";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);        // full report detail
  const [month, setMonth] = useState(() => {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const m = d.getMonth();
    return `${m === 0 ? d.getFullYear() - 1 : d.getFullYear()}-${String(m === 0 ? 12 : m).padStart(2, "0")}`;
  });
  const [audience, setAudience] = useState("board");
  const [editCom, setEditCom] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { setItems((await cmdApi.get("/reports")).data.items); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not load reports."); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const openReport = async (id) => {
    try { setOpen((await cmdApi.get(`/reports/${id}`)).data); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not open report."); }
  };

  const build = async () => {
    setBusy("build");
    try {
      const { data } = await cmdApi.post("/reports", { month, audience });
      toast.success("Draft built");
      await load();
      setOpen(data);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Build failed."); }
    finally { setBusy(""); }
  };

  const rebuild = async () => {
    setBusy("rebuild");
    try {
      const { data } = await cmdApi.post(`/reports/${open.id}/rebuild`);
      setOpen(data); toast.success("Snapshot rebuilt");
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Rebuild failed."); }
    finally { setBusy(""); }
  };

  const draftAI = async () => {
    setBusy("ai");
    try {
      const { data } = await cmdApi.post(`/reports/${open.id}/commentary`);
      setOpen(data); toast.success("Commentary drafted — review before publishing");
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Couldn't draft commentary, try again"); }
    finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      const { data } = await cmdApi.post(`/reports/${open.id}/publish`);
      setOpen(data); toast.success("Published — visible to investors if the audience allows");
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Publish failed."); }
    finally { setBusy(""); }
  };

  const remove = async (rep) => {
    try {
      await cmdApi.delete(`/reports/${rep.id}`);
      toast.success(rep.status === "published" ? "Archived" : "Deleted");
      if (open?.id === rep.id) setOpen(null);
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not remove."); }
  };

  const download = async (rep, kind) => {
    try {
      const { data } = await cmdApi.get(`/reports/${rep.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `eepl-${rep.audience}-report-${rep.month}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Download failed."); }
  };

  return (
    <section data-testid="cmd-reports">
      {canEdit && (
        <div className="cmd-toolbar" style={{ alignItems: "center" }}>
          <input type="month" className="cmd-input cmd-filter" value={month}
            onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
          <div className="cmd-view-toggle">
            {Object.entries(AUD_LABEL).map(([k, l]) => (
              <button key={k} className={`cmd-view-btn ${audience === k ? "active" : ""}`}
                onClick={() => setAudience(k)} data-testid={`audience-${k}`}>{l}</button>
            ))}
          </div>
          <button className="cmd-btn cmd-btn-primary" onClick={build} disabled={busy === "build"}
            data-testid="build-report" style={{ marginLeft: "auto" }}>
            <Plus size={15} style={{ marginRight: 5 }} />{busy === "build" ? "Building…" : "Build report"}
          </button>
        </div>
      )}

      <div className="cmd-reports-layout">
        <div className="cmd-reports-list" data-testid="reports-library">
          <h3 className="cmd-vertical-head">{isInvestor ? "Published reports" : "Library"}</h3>
          {items === null ? <p className="cmd-muted">Loading…</p>
            : items.length === 0 ? (
              <div className="cmd-empty" data-testid="reports-empty">
                <h3>No reports yet</h3>
                <p>{canEdit ? "Build your first monthly report above." : "Published reports will appear here."}</p>
              </div>
            ) : items.map((rep) => (
              <div key={rep.id} className={`cmd-report-row ${open?.id === rep.id ? "sel" : ""}`} data-testid={`report-row-${rep.id}`}>
                <button className="cmd-report-open" onClick={() => openReport(rep.id)} data-testid={`report-open-${rep.id}`}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{rep.title}</div>
                  <div className="cmd-muted" style={{ fontSize: 12 }}>
                    {AUD_LABEL[rep.audience]} · v{rep.version} · {fmtDateTime(rep.created_at)}
                  </div>
                </button>
                <div className="cmd-report-row-actions">
                  <span className={`cmd-pill ${rep.status === "published" ? "health-ok" : rep.status === "archived" ? "" : "stage-building"}`}
                    data-testid={`report-status-${rep.id}`}>
                    {rep.status === "published" ? "Published" : rep.status === "archived" ? "Archived" : "Draft"}
                  </span>
                  <button className="cmd-icon-btn" title="Download PDF" onClick={() => download(rep, "pdf")}
                    data-testid={`report-pdf-${rep.id}`}><Download size={14} /></button>
                  <button className="cmd-icon-btn" title="Download deck" onClick={() => download(rep, "pptx")}
                    data-testid={`report-pptx-${rep.id}`}><Download size={14} /></button>
                  {canEdit && (rep.status !== "published" || isFounder) && (
                    <button className="cmd-icon-btn" title={rep.status === "published" ? "Archive" : "Delete"}
                      onClick={() => remove(rep)} data-testid={`report-del-${rep.id}`}>
                      {rep.status === "published" ? <Archive size={14} /> : <Trash2 size={14} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>

        {open && (
          <div className="cmd-reports-detail">
            {canEdit && open.status !== "published" && (
              <div className="cmd-toolbar" style={{ marginBottom: 12 }}>
                <button className="cmd-btn" onClick={rebuild} disabled={busy === "rebuild"} data-testid="rebuild-report">
                  <RotateCcw size={14} style={{ marginRight: 5 }} />{busy === "rebuild" ? "Rebuilding…" : "Rebuild snapshot"}
                </button>
                <button className="cmd-btn" onClick={draftAI} disabled={busy === "ai"} data-testid="draft-commentary">
                  <Sparkles size={14} style={{ marginRight: 5 }} />{busy === "ai" ? "Drafting…" : "Draft commentary with AI"}
                </button>
                <button className="cmd-btn" onClick={() => setEditCom(true)} data-testid="edit-commentary">
                  Edit commentary
                </button>
                <button className="cmd-btn cmd-btn-primary" onClick={publish} disabled={busy === "publish"} data-testid="publish-report">
                  <Send size={14} style={{ marginRight: 5 }} />{busy === "publish" ? "Publishing…" : "Publish"}
                </button>
              </div>
            )}
            <ReportPreview report={open} />
          </div>
        )}
      </div>

      {editCom && open && (
        <Sheet onClose={() => setEditCom(false)} title="Edit commentary" testId="commentary-sheet" wide>
          <CommentaryEditor report={open} onSaved={async () => {
            setEditCom(false);
            await openReport(open.id);
            load();
          }} onClose={() => setEditCom(false)} />
        </Sheet>
      )}
    </section>
  );
}
